<template>
  <div class="export">
    <div class="export__boards">
      <div
        v-for="board in boards"
        ref="boards"
        :key="board.id"
        class="export__board"
      >
        <BoardPreview
          presentation-mode
          :template-id="board.layout"
          :board-id="board.id"
          :image-resolution="imageResolution"
        />
      </div>
    </div>
    <StitchDialog
      visible
      center
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      title="Export boards"
    >
      <div class="export__progress-body">
        <ElProgress
          class="progress"
          type="dashboard"
          :percentage="progressPercentage"
          :status="progressPercentage === 100 ? 'success' : null"
        />

        <h2 v-if="progressPercentage === 100">
          Your boards are ready...<br>
          Your PDF will be <br>
          automatically downloaded.
        </h2>
        <div v-else>
          <h2>Preparing the boards... this may take several minutes</h2>

          <h2 v-if="boardsConvertedCount > 0">
            Exported {{ boardsConvertedCount }} of {{ boards.length }}.
          </h2>
        </div>
      </div>
      <div
        v-if="progressPercentage === 100"
        slot="footer"
        class="export__progress-footer"
      >
        <router-link :to="`/projects/${projectId}/boards`">
          <ElButton type="text">
            Go back to overview
          </ElButton>
        </router-link>
      </div>
    </StitchDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Projects } from '@/mixins/projects'
import { imageResolutions } from '@/constants/image'
import { S3Trim } from '@/mixins/s3Trim.js'
import BoardPreview from '@/components/project/BoardPreview.vue'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'

export default {
  name: 'ExportBoards',

  components: {
    BoardPreview
  },

  mixins: [Projects, S3Trim],

  data: () => ({
    isLoadingBoards: true,
    boardsLoadedCount: 0,
    boardsConvertedCount: 0,
    validQualityValues: [1, 2, 4],
    pdfQuality: null,
    pdf: null
  }),

  computed: {
    ...mapGetters(['getOptionsByBoardId']),

    /**
     * @returns {number}
     */
    progressPercentage () {
      if (this.boards.length > 0) {
        return Math.round(
          ((this.boardsLoadedCount + this.boardsConvertedCount) /
            (this.boards.length * 2)) *
            100
        )
      } else {
        return 0
      }
    },

    /**
     * @returns {string}
     */
    imageResolution () {
      return imageResolutions.STYLE_FULL
    }
  },

  async created () {
    this.pdfQuality = this.$remoteSettings.defaultPdfQuality

    if (this.$route.query.quality) {
      const qualityFromQueryString = parseInt(this.$route.query.quality, 10)

      if (this.validQualityValues.includes(qualityFromQueryString)) {
        this.pdfQuality = qualityFromQueryString
      }
    }

    await this.fetchProject({
      projectId: this.projectId
    })

    await this.loadBoards()
  },

  methods: {
    ...mapActions(['fetchProject', 'fetchBoard']),

    /**
     */
    async loadBoards () {
      for (const board of this.boards) {
        await this.fetchBoard({ projectId: this.projectId, boardId: board.id })

        const boardImages = this.getOptionsByBoardId(board.id).map(item => {
          return {
            url: this.s3Trim(item.assets_file, this.imageResolution),
            styleId: item.style_id,
            boardId: board.id
          }
        })

        if (boardImages.length > 0) {
          this.loadImages(boardImages)
        } else {
          this.incrementBoardsLoadedCount()
        }
      }

      this.isLoadingBoards = false
    },

    /**
     * @param {string[]} imageUrls
     */
    loadImages (imageUrls) {
      const totalImages = imageUrls.length
      let imageCount = 0
      imageUrls.forEach(imageUrl => {
        const image = new Image()
        image.crossOrigin = 'use-credentials'
        image.onload = () => {
          imageCount++

          if (imageCount === totalImages) {
            this.incrementBoardsLoadedCount()
          }
        }

        image.src = imageUrl.url
      })
    },

    /**
     */
    incrementBoardsLoadedCount () {
      this.boardsLoadedCount++

      if (this.boardsLoadedCount === this.boards.length) {
        this.createExportPDF()
      }
    },

    /**
     */
    async createExportPDF () {
      await this.convertImagesToBase64()

      this.pdfDoc = new JsPDF({
        format: 'a4',
        orientation: 'landscape'
      })

      for (let index = 0; index < this.$refs.boards.length; index++) {
        const boardSnapshotCanvas = await this.createBoardSnapshot(
          this.$refs.boards[index]
        )

        const imageWidth =
          (boardSnapshotCanvas.width / boardSnapshotCanvas.height) *
          this.pdfDoc.internal.pageSize.getHeight()
        const imageMarginLeft =
          (this.pdfDoc.internal.pageSize.getWidth() - imageWidth) / 2

        this.pdfDoc.addImage(
          boardSnapshotCanvas,
          'PNG',
          imageMarginLeft,
          0,
          imageWidth,
          this.pdfDoc.internal.pageSize.getHeight()
        )

        if (index !== this.$refs.boards.length - 1) {
          this.pdfDoc.addPage()
        }

        this.boardsConvertedCount++
      }

      this.downloadExportPDF()
    },

    /**
     * @param   {Element} image
     * @param   {Element} canvas
     *
     * @returns {Promise}
     */
    loadOversizedCanvasImage (image, canvas) {
      const sizeMultiplier = this.pdfQuality

      return new Promise(resolve => {
        const widthMultiplied = image.naturalWidth * sizeMultiplier
        const heightMultiplied = image.naturalHeight * sizeMultiplier

        canvas.width = widthMultiplied
        canvas.height = heightMultiplied
        const context = canvas.getContext('2d')
        context.drawImage(image, 0, 0, widthMultiplied, heightMultiplied)

        image.onload = () => resolve()
        image.onerror = () => resolve({ status: 'error', image })

        image.src = canvas.toDataURL()
      })
    },

    /**
     * @returns {Promise[]}
     */
    convertImagesToBase64 () {
      const imagesArray = Array.from(document.querySelectorAll('img'))
      const canvas = document.createElement('canvas')
      const imagePromises = imagesArray.map((image) => this.loadOversizedCanvasImage(image, canvas))

      return Promise.all(imagePromises)
    },

    /**
     * @param   {Element} boardReference
     *
     * @returns {Promise}
     */
    createBoardSnapshot (boardReference) {
      return html2canvas(boardReference, {
        scale: this.pdfQuality
      })
    },

    /**
     */
    downloadExportPDF () {
      const downloadDate = new Date()
      const formattedDate =
        downloadDate.getDate() +
        '-' +
        (downloadDate.getMonth() + 1) +
        '-' +
        downloadDate.getFullYear()

      this.pdfDoc.save(`${this.project.name} - ${formattedDate}.pdf`)
    }
  }
}
</script>

<style lang="scss" scoped>
$a4-margin-left: 130px;
$a4-landscape-width: 29.7cm;
$a4-landscape-height: 21cm;

.export__boards {
  width: $a4-landscape-width;
  height: $a4-landscape-height;
  margin-left: $a4-margin-left;
  vertical-align: middle;
  background-color: $white;

  /deep/ .el-loading-mask {
    display: none;
  }
}

.export__board {
  width: $a4-landscape-width;
  height: $a4-landscape-height;
  padding: $spacer-unit-x4;
}

.export__progress-body,
.export__progress-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.export__progress-body {
  width: 100%;

  .progress {
    margin-bottom: $spacer-unit-x2;
  }
}
</style>

<style lang="scss">
@media print {
  .el-popup-parent--hidden {
    .el-dialog__wrapper,
    .v-modal {
      display: none;
    }
  }
}
</style>
